/*
 * hyperion-index:begin
 * ---
 * path: hyperion-cloud/web/src/platform/design-system/components/ui/SourcePill.tsx
 * layer: web
 * area: components
 * kind: component
 * web_root: components
 * relpath: web/src/platform/design-system/components/ui/SourcePill.tsx
 * filename: SourcePill.tsx
 * extension: tsx
 * ---
 * hyperion-index:end
 */

import { HTMLAttributes, ReactNode } from 'react';
import { ArrowTopRightOnSquareIcon, LinkIcon } from '@heroicons/react/20/solid';
import { cn } from '@/platform/shared/lib/utils';
import { describeSource, describeRequiredSource, type ProvenanceSource } from './sourceProvenance';

import { getDisplayTimeZone } from '@/platform/shared/lib/timezone';
export type KnownSource = ProvenanceSource | 'cerberus';
export type SourceId = KnownSource | (string & {});
export type SourcePillSize = 'sm' | 'md';

export interface SourcePillProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  source: SourceId;
  /** Required integrations describe dependencies, not evidence of collection. */
  context?: 'origin' | 'requirement';
  /** Optional record reference, shown alongside the source description. */
  label?: string;
  href?: string;
  size?: SourcePillSize;
  icon?: ReactNode;
  /** Collection timestamp only; do not pass a record creation or edit date. */
  observedAt?: string | null;
  /** The caller owns freshness policy; a timestamp alone never implies fresh evidence. */
  freshness?: 'fresh' | 'stale' | 'unknown';
  confidence?: 'verified' | 'inferred' | 'unknown';
}

export function SourcePill({
  source,
  context = 'origin',
  label,
  href,
  size = 'md',
  icon,
  className,
  observedAt,
  freshness = 'unknown',
  confidence,
  ...rest
}: SourcePillProps) {
  const isEvidence = context === 'origin';
  const description = isEvidence ? describeSource(source) : describeRequiredSource(source);
  const reference = label?.trim();
  const display = reference ? `${description} · ${reference}` : description;
  const timestamp =
    observedAt && Number.isFinite(Date.parse(observedAt))
      ? new Date(observedAt).toISOString()
      : null;
  const state =
    freshness === 'stale'
      ? 'Stale evidence'
      : freshness === 'fresh' && timestamp
        ? 'Fresh evidence'
        : 'Freshness unknown';
  const confidenceLabel =
    confidence === 'verified'
      ? 'Verified evidence'
      : confidence === 'inferred'
        ? 'Inferred evidence'
        : confidence === 'unknown'
          ? 'Confidence unknown'
          : null;
  const details = !isEvidence
    ? 'Required integration; connection status not supplied'
    : [state, timestamp ? `Observed ${timestamp}` : 'Observation time unknown', confidenceLabel]
        .filter(Boolean)
        .join(' · ');
  const iconSize = size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5';
  const content = (
    <>
      <span className={cn('inline-flex shrink-0 items-center', iconSize)} aria-hidden>
        {icon ?? <LinkIcon className={iconSize} />}
      </span>
      <span className="min-w-0 [overflow-wrap:anywhere]">{display}</span>
      <>{isEvidence && <span className="min-w-0 [overflow-wrap:anywhere]">{state}</span>}</>
      {isEvidence && timestamp && (
        <time dateTime={timestamp}>
          Observed{' '}
          {new Date(timestamp).toLocaleString(undefined, { timeZone: getDisplayTimeZone(),
            dateStyle: 'medium',
            timeStyle: 'short',
          })}
        </time>
      )}
      {isEvidence && confidenceLabel && <span>{confidenceLabel}</span>}
      {href && <ArrowTopRightOnSquareIcon aria-hidden className={cn('shrink-0', iconSize)} />}
    </>
  );
  const common = {
    ...rest,
    'aria-label': `${display} · ${details}`,
    'data-source': source,
    'data-freshness': !isEvidence
      ? undefined
      : state === 'Fresh evidence'
        ? 'fresh'
        : freshness === 'stale'
          ? 'stale'
          : 'unknown',
    title: `${display} · ${details}`,
    className: cn(
      'inline-flex max-w-full flex-wrap items-center gap-1.5 rounded-full font-medium tracking-tight',
      'ring-1 ring-inset ring-slate-500/30 bg-slate-500/10 text-slate-700 dark:text-slate-200',
      size === 'sm' ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-xs',
      href &&
        'hover:bg-slate-500/20 transition-colors motion-reduce:transition-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
      className
    ),
  };
  return href ? (
    <a {...(common as HTMLAttributes<HTMLAnchorElement>)} href={href}>
      {content}
    </a>
  ) : (
    <span {...common} role="img">
      {content}
    </span>
  );
}
